import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Eye, EyeOff, TrendingUp, TrendingDown, Wallet, Plus, Zap,
} from "lucide-react";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

// FIN-1 — HeroSaldoCard
// Card heroico com saldo total, toggle olho persistido em localStorage,
// 3 mini-cards (entradas/saídas/economia) abaixo, glow contextual de
// saúde (emerald se positivo crescente, coral se negativo decrescente).

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

function HiddenAmount() {
  return <span className="font-mono">R$ ••••••</span>;
}

function MiniCard({ icon: Icon, label, value, hidden, tone = "cyan", testId }) {
  const TONE_BG = {
    emerald: "bg-brand-green/10 text-brand-green",
    error:   "bg-status-error/10 text-status-error",
    cyan:    "bg-brand-cyan/10 text-brand-cyan",
  };
  return (
    <div
      className="glass-card glow-hover rounded-xl p-4 flex items-center gap-3"
      data-testid={testId}
    >
      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0", TONE_BG[tone])}>
        <Icon className="w-5 h-5" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <p className="text-micro text-foreground-subtle uppercase" style={{ letterSpacing: "0.08em" }}>
          {label}
        </p>
        <p className="text-base font-semibold tabular-nums text-foreground truncate">
          {hidden ? <HiddenAmount /> : fmtBRL(value)}
        </p>
      </div>
    </div>
  );
}

export default function HeroSaldoCard({
  totalBalance,    // shape: { total_balance, accounts_count, by_type, vs_previous_month: { delta_pct, delta, previous_balance } }
  summary,         // shape: { total_income, total_expenses, balance, ... }
  loading,
  onConnectBank,
  onCreateAccount,
}) {
  const { user } = useAuth();
  const userId = user?.id;
  const storageKey = userId ? `nexo_visao_balance_hidden_${userId}` : null;

  const [hidden, setHidden] = useState(() => {
    if (!storageKey) return false;
    try { return localStorage.getItem(storageKey) === "1"; }
    catch { return false; }
  });

  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, hidden ? "1" : "0"); }
    catch { /* ignore */ }
  }, [hidden, storageKey]);

  const balance = Number(totalBalance?.total_balance ?? 0);
  const accountsCount = Number(totalBalance?.accounts_count ?? 0);
  const deltaPct = Number(totalBalance?.vs_previous_month?.delta_pct ?? 0);
  const isPositive = balance >= 0;
  const isGrowing = deltaPct >= 0;

  const income = Number(summary?.total_income ?? 0);
  const expense = Number(summary?.total_expenses ?? 0);
  const savings = income - expense;

  // Glow contextual via box-shadow inline + pulse animado por sinal
  // de saúde. emerald pulsando se positivo+crescente, coral pulsando se
  // negativo. Cyan estático no caso neutro. Respeita reduce-motion via
  // motion-safe (motion-reduce remove a animação).
  const glowSubtle = isPositive && isGrowing
    ? "0 0 32px rgba(0, 255, 163, 0.20), 0 0 64px rgba(0, 255, 163, 0.08)"
    : !isPositive
      ? "0 0 32px rgba(255, 77, 109, 0.18), 0 0 64px rgba(255, 77, 109, 0.08)"
      : "0 0 24px rgba(0, 212, 255, 0.16)";
  const glowStrong = isPositive && isGrowing
    ? "0 0 48px rgba(0, 255, 163, 0.34), 0 0 96px rgba(0, 255, 163, 0.14)"
    : !isPositive
      ? "0 0 48px rgba(255, 77, 109, 0.30), 0 0 96px rgba(255, 77, 109, 0.12)"
      : "0 0 32px rgba(0, 212, 255, 0.24)";
  const shouldPulse = (isPositive && isGrowing) || !isPositive;

  const deltaIcon = deltaPct < 0 ? TrendingDown : TrendingUp;
  const DeltaIcon = deltaIcon;
  const deltaColor = deltaPct < 0 ? "text-status-error" : "text-brand-green";
  const deltaLabel = deltaPct < 0 ? "abaixo" : deltaPct > 0 ? "acima" : "igual";

  if (loading && !totalBalance) {
    return (
      <Card variant="elevated" className="glass-card-strong p-6 md:p-8 min-h-[260px] animate-pulse" data-testid="hero-saldo-skeleton">
        <div className="h-4 w-40 bg-surface-card-hover rounded mb-6" />
        <div className="h-20 w-72 bg-surface-card-hover rounded mb-4" />
        <div className="h-4 w-48 bg-surface-card-hover rounded" />
      </Card>
    );
  }

  return (
    <motion.div
      animate={shouldPulse ? { boxShadow: [glowSubtle, glowStrong, glowSubtle] } : { boxShadow: glowSubtle }}
      transition={shouldPulse ? { duration: 3.2, repeat: Infinity, ease: "easeInOut" } : { duration: 0 }}
      className="rounded-xl"
      data-testid="hero-saldo-glow-wrapper"
    >
    <Card
      variant="elevated"
      className="relative overflow-hidden glass-card-strong gradient-border-cyan glow-hover p-6 md:p-8"
      data-testid="hero-saldo"
    >
      {/* Header: eyebrow + bancos badge */}
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <span
          className="text-caption font-semibold uppercase text-brand-green/80 inline-flex items-center gap-2"
          style={{ letterSpacing: "0.12em" }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-brand-green" aria-hidden="true" />
          Saldo total em conta
        </span>

        {accountsCount > 0 ? (
          <Badge variant="outline" className="text-brand-cyan border-brand-cyan/30 bg-brand-cyan/5">
            <Zap className="w-3 h-3 mr-1" />
            {accountsCount} {accountsCount === 1 ? "banco" : "bancos"}
          </Badge>
        ) : (
          <button
            type="button"
            onClick={onConnectBank || onCreateAccount}
            className="inline-flex items-center gap-1.5 text-caption text-brand-cyan hover:text-brand-cyan/80 transition-colors"
            data-testid="hero-saldo-cta-add-bank"
          >
            <Plus className="w-3.5 h-3.5" />
            Conectar banco
          </button>
        )}
      </div>

      {/* Valor heroic + toggle olho */}
      <div className="flex items-end gap-4 mb-3 flex-wrap">
        <motion.h2
          key={hidden ? "hidden" : "shown"}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
          className={cn(
            "font-display font-extrabold tabular-nums leading-none tracking-[-0.02em]",
            "text-[64px] md:text-[80px] lg:text-[96px]",
            isPositive ? "text-foreground" : "text-status-error",
          )}
          data-testid="hero-saldo-value"
        >
          {hidden ? <HiddenAmount /> : fmtBRL(balance)}
        </motion.h2>
        <button
          type="button"
          onClick={() => setHidden(h => !h)}
          aria-label={hidden ? "Mostrar saldo" : "Ocultar saldo"}
          aria-pressed={hidden}
          className="p-2 rounded-md text-foreground-muted hover:text-brand-cyan hover:bg-brand-cyan/10 transition-colors mb-3"
          data-testid="hero-saldo-toggle"
        >
          {hidden ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>

      {/* Badge delta vs mês anterior */}
      <Badge
        variant="outline"
        className={cn(
          "border-subtle bg-transparent inline-flex items-center gap-1",
          deltaColor,
        )}
        data-testid="hero-saldo-delta"
      >
        <DeltaIcon className="w-3 h-3" />
        {deltaPct === 0 ? "sem variação" : `${Math.abs(deltaPct).toFixed(1)}% ${deltaLabel} vs mês anterior`}
      </Badge>

      {/* 3 mini-cards: entradas / saídas / economia */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <MiniCard icon={TrendingUp} label="Entradas" value={income} hidden={hidden} tone="emerald" testId="hero-mini-income" />
        <MiniCard icon={TrendingDown} label="Saídas" value={expense} hidden={hidden} tone="error" testId="hero-mini-expense" />
        <MiniCard
          icon={Wallet}
          label="Economia"
          value={savings}
          hidden={hidden}
          tone={savings >= 0 ? "emerald" : "error"}
          testId="hero-mini-savings"
        />
      </div>
    </Card>
    </motion.div>
  );
}
